/**
 * Default role permissions for local UI simulation/testing.
 * These should match the RBACService DEFAULT_ROLES on the backend.
 */
export const SIMULATED_ROLES = {
    super_admin: {
        name: 'Super Administrator',
        description: 'Full system access with all permissions',
        is_superuser: true,
        permissions: {} // Not needed if is_superuser is true
    },
    business_admin: {
        name: 'Business Administrator',
        description: 'Business operations and product management',
        is_superuser: false,
        permissions: {
            'dashboard.read': true, 'dashboard.export': true,
            'products.create': true, 'products.read': true, 'products.update': true, 'products.delete': true,
            'products.bulk_import': true, 'products.bulk_export': true,
            'categories.create': true, 'categories.read': true, 'categories.update': true, 'categories.delete': true,
            'collections.create': true, 'collections.read': true, 'collections.update': true, 'collections.delete': true,
            'customers.create': true, 'customers.read': true, 'customers.update': true, 'customers.assign_groups': true,
            'pricing.create': true, 'pricing.read': true, 'pricing.update': true, 'pricing.customer_overrides': true,
            'catalogues.create': true, 'catalogues.read': true, 'catalogues.update': true, 'catalogues.assign_products': true,
            'catalogues.manage_visibility': true,
            'inventory.read': true, 'inventory.update': true, 'inventory.adjust': true,
            'reports.sales': true, 'reports.inventory': true, 'reports.customers': true, 'reports.pricing': true, 'reports.export': true,
            'cms.create': true, 'cms.read': true, 'cms.update': true, 'cms.publish': true
        }
    },
    sales_admin: {
        name: 'Sales Administrator',
        description: 'Customer and order management',
        is_superuser: false,
        permissions: {
            'dashboard.read': true,
            'customers.create': true, 'customers.read': true, 'customers.update': true, 'customers.assign_groups': true,
            'orders.create': true, 'orders.read': true, 'orders.update': true, 'orders.bulk_process': true,
            'pricing.read': true, 'pricing.customer_overrides': true,
            'catalogues.read': true, 'catalogues.assign_products': true,
            'inventory.read': true,
            'reports.sales': true, 'reports.customers': true, 'reports.pricing': true, 'reports.export': true,
            'notifications.send': true
        }
    },
    billing_admin: {
        name: 'Billing Administrator',
        description: 'Payment and financial management',
        is_superuser: false,
        permissions: {
            'customers.read': true,
            'orders.read': true, 'orders.update': true,
            'payments.read': true, 'payments.process': true, 'payments.reconcile': true, 'payments.refund': true,
            'reports.financial': true, 'reports.export': true,
            'admin.audit.read': true, 'admin.compliance.read': true
        }
    },
    dispatch_admin: {
        name: 'Dispatch Administrator',
        description: 'Shipping and fulfillment management',
        is_superuser: false,
        permissions: {
            'orders.read': true, 'orders.manage_status': true,
            'shipping.create': true, 'shipping.read': true, 'shipping.update': true, 'shipping.track': true,
            'shipping.generate_awb': true,
            'inventory.read': true, 'inventory.update': true,
            'notifications.send': true
        }
    },
    it_admin: {
        name: 'IT Administrator',
        description: 'System administration and technical management',
        is_superuser: false,
        permissions: {
            'admin.users.create': true, 'admin.users.read': true, 'admin.users.update': true, 'admin.users.delete': true,
            'admin.roles.create': true, 'admin.roles.read': true, 'admin.roles.update': true, 'admin.roles.delete': true,
            'admin.system.config': true, 'admin.integrations': true,
            'admin.audit.read': true, 'admin.audit.export': true, 'admin.audit.admin': true,
            'admin.compliance.read': true, 'admin.compliance.generate': true,
            'admin.system.health': true, 'admin.system.alerts': true,
            'notifications.config': true, 'notifications.templates': true
        }
    },
    viewer: {
        name: 'Viewer',
        description: 'Read-only access to most data',
        is_superuser: false,
        permissions: {
            'products.read': true, 'categories.read': true, 'collections.read': true, 'customers.read': true, 'orders.read': true,
            'pricing.read': true, 'catalogues.read': true, 'inventory.read': true, 'payments.read': true,
            'shipping.read': true, 'reports.sales': true, 'reports.inventory': true, 'reports.customers': true, 'reports.pricing': true,
            'cms.read': true
        }
    }
} as const;

export type SimulatedRoleKey = keyof typeof SIMULATED_ROLES;
